/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types';

import React from 'react';
import Localized from '../../../shared/components/Localized';
import _ from 'lodash';
import Modal from './Modal';
import ConfirmationModal from './ConfirmationModal';
import styled from 'styled-components';
import { Input } from '../Form';
import Button from '../Button';

const ModelDiv = styled.div`
  height: 29.5625rem;
  object-fit: contain;
  @media (min-width: 20rem) and (max-width: 30rem) {
    width: 18rem;
    margin-right: 1rem;
    margin-left: 1rem;
    height: 26.0625rem;
    object-fit: contain;
  }
`;
const ModelHeader = styled.div`
  display: flex;
  flex-direction: column;
  height: 4.75rem;
  border-radius: 0.25rem;
  box-shadow: 0 0.125rem 0 0 #f0f2f7;
  background-color: #ffffff;
  justify-content: center;
  align-items: center;
  @media (min-width: 20rem) and (max-width: 30rem) {
    width: 20rem;
    height: 4.125rem;
  }
`;
const ModelHeading = styled.p`
  margin-top: 1rem;
  font-family: 'Salesforce Sans';
  font-size: 1.25rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #2b2826;
  @media (min-width: 20rem) and (max-width: 30rem) {
    width: 20rem;
    height: 4.125rem;
    font-size: 1.125rem;
    text-align: center;
  }
`;
const ModelSubHeader = styled.p`
  font-family: 'Salesforce Sans';
  font-size: 0.75rem;
  padding-bottom: 1rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  margin-top: 0.5rem;
  line-height: 1.25;
  letter-spacing: normal;
  text-align: center;
  color: #706e6b;
`;
const ModelIntro = styled.p`
  margin-top: 2.25rem;
  margin-left: 6rem;
  font-family: 'Salesforce Sans';
  font-size: 0.75rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: #706e6b;
  @media (min-width: 20rem) and (max-width: 30rem) {
    width: 16rem;
    color: #706e6b;
    margin-top: 1.125rem;
    margin-left: 1rem;
  }
`;
const ErrorMessage = styled.p`
  font-family: Salesforce Sans;
  font-size: 0.75rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #c23934;
  margin-left: 6rem;
  margin-top: 0.25rem;
`;
const InputLabel = styled.p`
  margin-top: 1.6875rem;
  margin-left: 6rem;
  font-family: 'Salesforce Sans';
  font-size: 0.75rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 2.17;
  letter-spacing: 0.0313rem;
  color: #54698d;
  @media (min-width: 20rem) and (max-width: 30rem) {
    margin-top: 1.125rem;
    margin-left: 1rem;
  }
`;
const EmailInput = styled.input`
  margin-top: 0.25rem;
  margin-left: 6rem;
  margin-bottom: 0.9375rem;
  width: 21.8125rem;
  height: 2.5rem;
  width: 16rem;
  @media (min-width: 20rem) and (max-width: 30rem) {
    width: 16rem;
    height: 2.5rem;
    margin-top: 1.125rem;
    margin-left: 1rem;
  }
`;
const SignupButton = styled(Button)`
  width: 14.25rem;
  height: 2rem;
  margin-left: 6rem;
  font-family: 'Salesforce Sans';
  font-size: 0.75rem;
  letter-spacing: normal;
  text-align: center;
  color: #ffffff;
  @media (min-width: 20rem) and (max-width: 30rem) {
    margin-top: 1.125rem;
    margin-left: 1rem;
  }
`;
export default class SignupModal extends React.Component {
  static propTypes = {
    submit: PropTypes.func,
    closeModal: PropTypes.func,
    instanceKey: PropTypes.string,
    requests: PropTypes.object,
    isOpen: PropTypes.bool
  };
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      emailValid: false,
      subscriptionValid: false,
      submitted: false
    };
    this.onEmailChange = this.onEmailChange.bind(this);
    this.onSubmitClick = this.onSubmitClick.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
  }
  onKeyPress(event) {
    if (event.key === 'Enter') {
      this.onSubmitClick();
    }
  }
  onEmailChange(event) {
    // If currently invalid then check for validity
    const email = event.target.value;
    const emailValidation = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const emailValid = email.match(emailValidation) !== null;
    this.setState({ email, emailValid });
  }
  onSubmitClick() {
    this.setState({ submitted: true });
    // If email or subscription is invalid do not submit
    if (!this.state.emailValid) return;
    const payload = {
      emailAddress: this.state.email
    };

    return this.props.requests.login.handleRequest({ body: payload });
  }
  componentWillUnmount() {
    this.props.requests.subscribe.handleReset();
  }
  render() {
    const {
      closeModal, requests, instanceKey, isOpen
    } = this.props;
    const { emailValid, subscriptionValid, submitted } = this.state;
    const locales = requests.locales.value;
    if (requests.login.isCompleted) {
      return (
        <ConfirmationModal
          closeModal={closeModal}
          emailAddress={this.state.email}
        />
      );
    }
    return (
      <div>
        <ModelDiv>
          <ModelHeader>
            <ModelHeading>
              <Localized
                modelName="signupModal"
                modelKey="headerModal"
                modelAttribute="value"
              />
            </ModelHeading>

            <ModelSubHeader>
              <Localized
                modelName={'instance'}
                modelKey={instanceKey}
                modelAttribute={'label'}
              />
            </ModelSubHeader>
          </ModelHeader>
          <ModelIntro>
            <Localized
              modelName="signupModal"
              modelKey="introModal"
              modelAttribute="value"
            />
          </ModelIntro>
          <InputLabel>
            <Localized
              modelName="signupModal"
              modelKey="emailAddress"
              modelAttribute="value"
            />
          </InputLabel>
          <EmailInput
            id="email"
            autoFocus
            className="slds-input"
            type="text"
            size="10"
            maxLength="100"
            onChange={this.onEmailChange}
            value={this.state.email}
            onKeyPress={this.onKeyPress}
            placeholder="email@example.com"
          />
          {submitted && !emailValid && (
            <ErrorMessage>
              <Localized
                modelName="signupModal"
                modelKey="errorMessage"
                modelAttribute="value"
              />{' '}
            </ErrorMessage>
          )}
          <br />
          <SignupButton onClick={this.onSubmitClick} theme="brand">
            <Localized
              modelName="signupModal"
              modelKey="headerButtonText"
              modelAttribute="value"
            />
          </SignupButton>
        </ModelDiv>
      </div>
    );
  }
}
